import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { RouterProvider } from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";
import { GC_TIME, idbPersister, PERSIST_MAX_AGE } from "../offline/queryPersister";
import { installReconnect } from "../offline/reconnect";
import { router } from "./router";
import { useMe } from "../api/hooks";
import { I18nProvider, browserLanguage, isLanguage } from "../i18n";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false, gcTime: GC_TIME, networkMode: "offlineFirst" },
    mutations: { networkMode: "offlineFirst" },
    // persist every query that holds data, including ones whose refetch failed offline (the
    // default keeps only successful queries and would drop the session/registry caches)
    dehydrate: { shouldDehydrateQuery: (q) => q.state.data !== undefined },
  },
});

export const persistOptions = { persister: idbPersister, maxAge: PERSIST_MAX_AGE, buster: "v1" };

// test/diagnostic aid: lets Playwright inspect the cache
(window as unknown as { __kssQueryClient?: QueryClient }).__kssQueryClient = queryClient;

export function App() {
  useEffect(() => {
    installReconnect(queryClient, () => void router.navigate({ to: "/login", search: { next: location.pathname } }));
  }, []);
  return (
    <PersistQueryClientProvider client={queryClient} persistOptions={{ persister: idbPersister, maxAge: PERSIST_MAX_AGE, buster: "v1" }}>
      <Localised>
        <RouterProvider router={router} context={{ queryClient }} />
      </Localised>
    </PersistQueryClientProvider>
  );
}

/** [M23] The interface language: the reader's own choice, or their browser's (spec §8.23, Q60).
 *  It lives inside the query provider because the choice travels on the account. */
function Localised({ children }: { children: ReactNode }) {
  const me = useMe();
  const chosen = me.data?.language;
  const language = isLanguage(chosen) ? chosen : browserLanguage();
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);
  return <I18nProvider language={language}>{children}</I18nProvider>;
}
